import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { Endpoint } from '@/Config/api/endpoint';
import { UserApi } from '@/Domain/api/User/User.api';
import { User } from '@/Domain/api/User/dto/User.dto';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';

const UserProfileValidator = z.object({
  first_name: z.string().max(150),
  last_name: z.string().max(150),
});

export type UserProfileForm = z.infer<typeof UserProfileValidator>;

export const useUserProfile = (user: User) => {
  const { t } = useTranslation('user_profile');
  const queryClient = useQueryClient();

  const form = useForm<UserProfileForm>({
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    },
    resolver: zodResolver(UserProfileValidator),
  });

  const mutation = useMutation({
    mutationKey: [`${Endpoint.user.update}-update`],
    mutationFn: (data: Partial<UserProfileForm>) => UserApi.update(data),
    onSuccess: (data) => {
      queryClient.setQueryData([Endpoint.user.myself], () => data);
    },
    onError: () => {
      toast.error(t('toast.error'));
    },
  });

  const handleUpdate = (data: Partial<UserProfileForm>) => mutation.mutate(data);

  useAutoSubmit<UserProfileForm>(
    form,
    (data: Partial<UserProfileForm>) => {
      handleUpdate(data);
    },
    false,
  );

  return {
    handleUpdate,
    form,
  };
};
